import NotificationsNoneOutlined from '@mui/icons-material/NotificationsNoneOutlined';

import { formatRelative } from '../components/EngagementHub/format';
import type { NotificationFeedView, NotificationRow } from './notificationFeed';

interface NotificationRowsProps {
  view: NotificationFeedView;
  loading: boolean;
  error: boolean;
  /** null = all engagements. */
  engagementId: string | null;
  unreadIds: string[];
  /** A failed read/dismiss mutation, surfaced in the panel. */
  actionError: string | null;
  onSelectEngagement: (id: string | null) => void;
  onActivate: (id: string, href: string | null) => void;
  onDismiss: (id: string) => void;
  onMarkVisibleRead: () => void;
  onRetry: () => void;
}

/**
 * The bell panel's contents (ENG-5451 / FE5) — presentation only; the container
 * owns every query and mutation.
 *
 * Structure follows the promoted prototype (`components/NotificationBell`) so
 * the two do not drift, with the prototype's three placeholders made real: the
 * filter narrows server-side, "Open" navigates, and read state persists.
 */
export default function NotificationRows({
  view,
  loading,
  error,
  engagementId,
  unreadIds,
  actionError,
  onSelectEngagement,
  onActivate,
  onDismiss,
  onMarkVisibleRead,
  onRetry,
}: NotificationRowsProps): JSX.Element {
  return (
    <div data-testid="engagement-notification-panel">
      <div className="flex items-center justify-between border-b border-surface-border px-16 py-12">
        <span className="text-on-surface subtitle-1">Notifications</span>
        {unreadIds.length > 0 ? (
          // Not "Mark all read": `unreadIds` is the one page of rows currently
          // shown, under whatever filter is active, while the badge counts the
          // whole unread set server-side. Claiming "all" would leave the badge
          // lit with nothing left to click. A real mark-all needs a
          // filter-scoped mutation the api does not have yet — ENG-5451 notes.
          <button type="button" onClick={onMarkVisibleRead} className="text-brand-primary body-2">
            Mark these read
          </button>
        ) : null}
      </div>

      {actionError ? (
        <p role="alert" className="border-b border-surface-border px-16 py-8 text-red-600 body-2">
          {actionError}
        </p>
      ) : null}

      <FilterBar
        engagements={view.engagements}
        active={engagementId}
        onChange={onSelectEngagement}
      />

      {error ? (
        <Message
          title="Notifications could not be loaded."
          body="This does not affect anything else on the page."
          action={
            <button type="button" onClick={onRetry} className="text-brand-primary body-2">
              Try again
            </button>
          }
        />
      ) : loading && view.rows.length === 0 ? (
        <div className="flex flex-col gap-8 p-16" aria-label="Loading notifications">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-44 animate-pulse rounded-md bg-surface-elevated" />
          ))}
        </div>
      ) : view.rows.length === 0 ? (
        <Message
          title="You&rsquo;re all caught up."
          body={
            engagementId
              ? 'No notifications for this engagement yet.'
              : 'New notifications show up here as work moves.'
          }
        />
      ) : (
        <ul className="max-h-[420px] list-none overflow-y-auto p-[0]">
          {view.rows.map((row) => (
            <Row key={row.id} row={row} onActivate={onActivate} onDismiss={onDismiss} />
          ))}
        </ul>
      )}
    </div>
  );
}

function Row({
  row,
  onActivate,
  onDismiss,
}: {
  row: NotificationRow;
  onActivate: (id: string, href: string | null) => void;
  onDismiss: (id: string) => void;
}): JSX.Element {
  return (
    <li
      data-testid="engagement-notification-row"
      className={`flex gap-12 border-b border-surface-border px-16 py-12 last:border-b-[0] ${
        row.read ? '' : 'bg-brand-primary/5'
      }`}
    >
      <span className="flex w-8 shrink-0 justify-center pt-6" aria-hidden>
        {row.read ? null : <span className="h-8 w-8 rounded-full bg-brand-primary" />}
      </span>

      <div className="flex min-w-0 flex-col gap-4">
        <span className="text-on-surface body-2">{row.copy.title}</span>
        {row.copy.detail ? (
          <span className="text-on-surface-muted body-2">{row.copy.detail}</span>
        ) : null}
        {row.copy.quote ? (
          <blockquote className="whitespace-pre-line border-l-2 border-surface-border pl-8 text-on-surface-muted body-2">
            {row.copy.quote}
          </blockquote>
        ) : null}

        <div className="flex flex-wrap items-center gap-8">
          <span className="text-on-surface-muted body-2">{row.engagementLabel}</span>
          <span className="text-on-surface-muted body-2" aria-hidden>
            &middot;
          </span>
          <span className="text-on-surface-muted body-2">{formatRelative(row.createdAt)}</span>
          {row.href ? (
            <button
              type="button"
              onClick={() => onActivate(row.id, row.href)}
              className="text-brand-primary body-2"
            >
              Open
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => onDismiss(row.id)}
            className="text-on-surface-muted body-2"
          >
            Dismiss
          </button>
        </div>
      </div>
    </li>
  );
}

/**
 * Per-engagement filter. A pill row rather than `componentsv2/Select`, which is
 * a multi-select autocomplete and the wrong shape for a single choice — the
 * third primitive gap the prototype flagged, left as flagged.
 *
 * Hidden below two engagements: "All" alone is noise. The guard is safe only
 * because the options are the viewer's whole engagement set and do not shrink
 * when a filter is applied — derived from the narrowed feed it used to unmount
 * the bar the moment someone used it, "All engagements" included (AC5).
 *
 * That set has no ceiling — it spans the closed bucket too — so the row is
 * capped at roughly five lines of pills and scrolls past that, rather than
 * pushing the feed out of the panel.
 */
function FilterBar({
  engagements,
  active,
  onChange,
}: {
  engagements: { id: string; label: string }[];
  active: string | null;
  onChange: (id: string | null) => void;
}): JSX.Element | null {
  if (engagements.length < 2) return null;

  const options: { value: string | null; label: string }[] = [
    { value: null, label: 'All engagements' },
    ...engagements.map((e) => ({ value: e.id, label: e.label })),
  ];

  return (
    <div
      className="flex max-h-[196px] flex-wrap items-center gap-8 overflow-y-auto border-b border-surface-border px-16 py-12"
      role="group"
      aria-label="Filter by engagement"
    >
      {options.map((o) => {
        const isActive = active === o.value;
        return (
          <button
            key={o.value ?? 'all'}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(o.value)}
            className={`min-h-[32px] rounded-full border px-12 py-4 transition-colors body-2 ${
              isActive
                ? 'border-brand-primary/50 bg-brand-primary/15 text-on-surface'
                : 'border-surface-border bg-surface-elevated text-on-surface-muted hover:bg-surface-secondary'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Message({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: JSX.Element;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-8 px-24 py-40 text-center" role="status">
      <NotificationsNoneOutlined sx={{ fontSize: 32 }} className="text-on-surface-muted" />
      <span className="text-on-surface body-2">{title}</span>
      <span className="max-w-[260px] text-on-surface-muted body-2">{body}</span>
      {action}
    </div>
  );
}
